import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { findKey, first, get } from 'lodash'
import { getFriendlyBoundaryName } from 'map-style-utils'
import { useSelector } from 'react-redux'

const BOUNDARIES_AGGREGATE_KEYS = [
  'MS_zz-countries',
  'MS_us-states',
  'MS_us-counties',
  'MS_us-zipcodes',
  'MS_uk-regions',
  'MS_uk-lads',
  'MS_uk-msoas',
  'MS_au-states',
  'MS_au-gdas',
  'MS_au-postcodes'
]

const AGGREGATE_SOURCE_LAYERS = {
  'MS_zz-countries': 'zz:countries',
  'MS_us-states': 'us:states',
  'MS_us-counties': 'us:counties',
  'MS_us-zipcodes': 'us:zipcodes',
  'MS_uk-regions': 'uk:regions',
  'MS_uk-lads': 'uk:lads',
  'MS_uk-msoas': 'uk:msoas',
  'MS_au-states': 'au:states',
  'MS_au-gdas': 'au:gdas',
  'MS_au-postcodes': 'au:postcodes'
}

type AggregateKeys = keyof typeof AGGREGATE_SOURCE_LAYERS
type Aggregates = (typeof AGGREGATE_SOURCE_LAYERS)[AggregateKeys]
type Display = '2D' | '3D'

type State = {
  options: typeof BOUNDARIES_AGGREGATE_KEYS
  aggregate: Aggregates
  display: Display
  aggregateProperty: string
}

const initialState: State = {
  options: ['zz:countries'],
  aggregate: 'zz:countries',
  display: '2D',
  aggregateProperty: 'count'
}

export const boundariesSlice = createSlice({
  name: 'boundaries',
  initialState,
  reducers: {
    setAggregateProperty: (state, action) => {
      state.aggregateProperty = action.payload
    },
    setAggregate: (state, action: PayloadAction<Aggregates>) => {
      state.aggregate = action.payload
    },
    setDisplay: (state, action: PayloadAction<Display>) => {
      state.display = action.payload
    },
    setBoundaryOptions: (
      state,
      action: PayloadAction<typeof BOUNDARIES_AGGREGATE_KEYS>
    ) => {
      const firstOption = first(action.payload) as string

      const defaultAggregate = get(
        AGGREGATE_SOURCE_LAYERS,
        firstOption,
        'zz:countries'
      )

      state.options = action.payload
      state.aggregate = defaultAggregate
    }
  }
})

export const selectAggregate = (state) => state.boundaries.aggregate

const selectDisplay = (state) => state.boundaries.display

export const selectAggregateProperty = (state) =>
  state.boundaries.aggregateProperty

const selectAggregateOptions = (state) => {
  const { options } = state.boundaries

  return options.map((value) => ({
    value: AGGREGATE_SOURCE_LAYERS[value],
    label: getFriendlyBoundaryName(value)?.plural
  }))
}

export const selectAggregateLabel = (state): string => {
  const { aggregate } = state.boundaries

  const key = findKey(AGGREGATE_SOURCE_LAYERS, (val) => val === aggregate)

  return getFriendlyBoundaryName(key).singular as string
}

export const useAggregate = () => useSelector(selectAggregate)
export const useDisplay = () => useSelector(selectDisplay)
export const useAggregateProperty = () => useSelector(selectAggregateProperty)
export const useAggregateOptions = () => useSelector(selectAggregateOptions)
export const useAggregateLabel = () => useSelector(selectAggregateLabel)

export const {
  setDisplay,
  setAggregate,
  setAggregateProperty,
  setBoundaryOptions
} = boundariesSlice.actions

export default boundariesSlice.reducer
